import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export const STAGES = ["mapeada", "primeiro_contato", "reuniao", "diagnostico", "proposta", "tramite", "contratada"] as const;
export const STAGE: StatusMap = {
  mapeada: { label: "mapeada", tone: "neutral" },
  primeiro_contato: { label: "primeiro contato", tone: "neutral" },
  reuniao: { label: "reunião", tone: "info" },
  diagnostico: { label: "diagnóstico", tone: "info" },
  proposta: { label: "proposta", tone: "warn" },
  tramite: { label: "em trâmite", tone: "warn" },
  contratada: { label: "contratada", tone: "ok" },
  perdida: { label: "perdida", tone: "crit" },
  suspensa: { label: "suspensa", tone: "neutral" },
};
export const STAGE_LABEL: Record<string, string> = {
  mapeada: "Mapeada", primeiro_contato: "Primeiro contato", reuniao: "Reunião", diagnostico: "Diagnóstico", proposta: "Proposta", tramite: "Em trâmite",
  contratada: "Contratada", perdida: "Perdida", suspensa: "Suspensa",
};
/** Etapas que exigem gestor (as demais, qualquer pessoa da equipe). */
export const MANAGER_STAGES = new Set(["proposta", "tramite", "contratada"]);
export const MODALITY: Record<string, string> = {
  pregao_eletronico: "Pregão eletrônico", pregao_presencial: "Pregão presencial", concorrencia: "Concorrência", dispensa: "Dispensa", inexigibilidade: "Inexigibilidade",
  credenciamento: "Credenciamento", dialogo_competitivo: "Diálogo competitivo", adesao_ata: "Adesão a ata", a_verificar: "A verificar",
};
export const CATEGORY: Record<string, string> = {
  assessoria_gestao_saude: "Assessoria em gestão da saúde", sistemas_informacao_saude: "Sistemas de informação em saúde", gestao_aps: "Gestão da APS",
  formacao_gestores: "Formação de gestores", pesquisa: "Pesquisa", outro: "Outro",
};
export const PHASE: Record<string, string> = { planejamento: "Planejamento (órgão)", habilitacao: "Habilitação (InPES)", contratacao: "Contratação", execucao: "Execução" };
export const DECISION: StatusMap = {
  sem_conflito: { label: "sem conflito", tone: "ok" },
  conflito_mitigado: { label: "conflito mitigado", tone: "warn" },
  impedido: { label: "impedido", tone: "crit" },
};
export const MEETING_STATUS: StatusMap = {
  agendada: { label: "agendada", tone: "info" },
  realizada: { label: "realizada", tone: "ok" },
  cancelada: { label: "cancelada", tone: "neutral" },
};
export const MISSING_LABEL: Record<string, string> = { "ANALISE-CONFLITO": "análise de conflito liberada", MODALIDADE: "modalidade definida" };
export const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

export interface PipelineRow {
  id: string;
  code: string;
  agency: string;
  municipality: string | null;
  uf: string | null;
  title: string;
  axes: string[];
  stage: string;
  expected_modality: string | null;
  estimated_value_cents: number | null;
  contact_person_id: string | null;
  contact_name: string | null;
  owner_label: string | null;
  next_step: string | null;
  next_step_on: string | null;
  lost_reason: string | null;
  updated_at: string;
  conflict_decision: string | null;
  required_items: number;
  required_done: number;
  missing_for_proposal: string[];
  last_meeting_at: string | null;
  next_meeting_at: string | null;
}
export interface Opportunity {
  id: string;
  code: string;
  agency: string;
  municipality: string | null;
  uf: string | null;
  title: string;
  scope: string | null;
  axes: string[];
  stage: string;
  expected_modality: string | null;
  estimated_value_cents: number | null;
  contact_person_id: string | null;
  owner_label: string | null;
  next_step: string | null;
  next_step_on: string | null;
  lost_reason: string | null;
}
export interface ChecklistRow {
  id: string;
  item_code: string;
  status: "pendente" | "atendido" | "nao_aplica";
  evidence: string | null;
  justification: string | null;
  checked_at: string | null;
}
export interface LawItem {
  code: string;
  phase: string;
  sort: number;
  text: string;
  legal_ref: string;
  required: boolean;
  gate_stage: string | null;
}
export interface Meeting {
  id: string;
  opportunity_id: string | null;
  agency: string;
  municipality: string | null;
  scheduled_at: string;
  held_at: string | null;
  mode: string;
  formal_channel: boolean;
  participants: Array<{ name: string; role?: string }>;
  agenda: string | null;
  summary: string | null;
  next_steps: string | null;
  status: string;
  canceled_reason: string | null;
}
export interface Conflict {
  id: string;
  opportunity_id: string | null;
  agency: string;
  answers: Record<string, boolean>;
  relationships: string | null;
  decision: string;
  mitigation: string | null;
  justification: string;
  created_at: string;
}
export interface Comparable {
  id: string;
  agency: string;
  municipality: string | null;
  uf: string | null;
  object: string;
  category: string;
  value_cents: number | null;
  modality: string;
  legal_basis: string | null;
  term_months: number | null;
  signed_on: string | null;
  year: number | null;
  supplier_name: string | null;
  source_label: string | null;
  source_url: string | null;
  evidence_ref: string | null;
  verified: boolean;
  notes: string | null;
}
export interface Question {
  key: string;
  text: string;
  blocking: boolean;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "inpes.create_opportunity": {
      args: {
        p_org: U; p_agency: string; p_title: string; p_municipality?: string | null; p_uf?: string | null; p_scope?: string | null; p_axes?: string[]; p_expected_modality?: string | null;
        p_estimated_value_cents?: number | null; p_contact_person?: U | null; p_owner_label?: string | null; p_next_step?: string | null; p_next_step_on?: string | null;
      };
      returns: U;
    };
    "inpes.update_opportunity": {
      args: {
        p_opportunity: U; p_title?: string | null; p_scope?: string | null; p_axes?: string[] | null; p_expected_modality?: string | null; p_estimated_value_cents?: number | null;
        p_contact_person?: U | null; p_owner_label?: string | null; p_next_step?: string | null; p_next_step_on?: string | null;
      };
      returns: null;
    };
    "inpes.move_stage": { args: { p_opportunity: U; p_stage: string; p_note?: string | null; p_lost_reason?: string | null }; returns: Record<string, unknown> };
    "inpes.missing_for_stage": { args: { p_opportunity: U; p_stage: string }; returns: string[] | null };
    "inpes.set_checklist_item": { args: { p_opportunity: U; p_item_code: string; p_status: string; p_evidence?: string | null; p_justification?: string | null }; returns: null };
    "inpes.record_conflict_analysis": {
      args: { p_org: U; p_agency: string; p_answers: Record<string, boolean>; p_decision: string; p_justification: string; p_mitigation?: string | null; p_relationships?: string | null; p_opportunity?: U | null; p_idempotency_key?: string | null };
      returns: U;
    };
    "inpes.conflict_questions": { args: Record<string, never>; returns: Question[] };
    "inpes.schedule_meeting": {
      args: { p_org: U; p_agency: string; p_scheduled_at: string; p_mode?: string; p_formal_channel?: boolean; p_agenda?: string | null; p_participants?: Array<{ name: string; role?: string }>; p_opportunity?: U | null; p_municipality?: string | null };
      returns: U;
    };
    "inpes.record_meeting": { args: { p_meeting: U; p_summary: string; p_next_steps?: string | null; p_held_at?: string | null; p_participants?: Array<{ name: string; role?: string }> | null }; returns: null };
    "inpes.cancel_meeting": { args: { p_meeting: U; p_reason: string }; returns: null };
    "inpes.upsert_comparable": {
      args: {
        p_org: U; p_agency: string; p_object: string; p_category: string; p_modality: string; p_value_cents?: number | null; p_municipality?: string | null; p_uf?: string | null; p_legal_basis?: string | null;
        p_term_months?: number | null; p_signed_on?: string | null; p_year?: number | null; p_supplier_name?: string | null; p_source_label?: string | null; p_source_url?: string | null;
        p_evidence_ref?: string | null; p_verified?: boolean; p_notes?: string | null; p_id?: U | null;
      };
      returns: U;
    };
  }
}

export function usePipeline(orgId: string) {
  return useQuery({ queryKey: ["inpes", "pipeline", orgId], queryFn: () => unwrap<PipelineRow[]>(from("inpes", "v_pipeline").select("*").eq("org_id", orgId).order("updated_at", { ascending: false })) });
}

export function useMeetings(orgId: string, opportunityId?: string) {
  return useQuery({
    queryKey: ["inpes", "meetings", orgId, opportunityId ?? "all"],
    queryFn: () => {
      let q = from("inpes", "meetings").select("*").eq("org_id", orgId);
      if (opportunityId) q = q.eq("opportunity_id", opportunityId);
      return unwrap<Meeting[]>(q.order("scheduled_at", { ascending: false }));
    },
  });
}

export function useLawItems() {
  return useQuery({ queryKey: ["inpes", "law_items"], staleTime: 3_600_000, queryFn: () => unwrap<LawItem[]>(from("inpes", "law_checklist_items").select("code, phase, sort, text, legal_ref, required, gate_stage").order("sort")) });
}

/** Participantes em texto: um por linha, "Nome | papel". */
export function parseParticipants(s: string): Array<{ name: string; role?: string }> {
  return s.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [name, role] = l.split("|").map((x) => x.trim());
    return role ? { name: name!, role } : { name: name! };
  });
}
